/**
 * EmployeePanel Component (New Design System)
 * 
 * Redesigned Employee management panel using the new design system components.
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BusinessIcon from '@mui/icons-material/Business';

// Design System Components
import {
  colors,
  EntityPageLayout,
  EntityPageHeader,
  InfoCard,
  DraftTextField,
  DraftSelectField,
  ReadOnlyField,
  Button,
} from '@components/design-system';
import { AppRoutes } from '@consts/routes';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import {
  useGetEmployeeQuery,
  useUpdateEmployeeMutation,
  useCreateEmployeeMutation,
} from '@features/redux/apis/employees';
import { useLocationMapQuery, useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';

// Draft state setup
const useEmployeeDraft = Query.withDraft(useGetEmployeeQuery, {
  Name: '',
  Email: '',
  Status: 'Active' as const,
  LocationID: null,
  CompanyID: null,
  ResellerID: null,
  EmployeeID: '',
});

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
];

export const EmployeePanel: React.FC<ManageProps & { id?: string }> = ({ operation, id, onSuccess }) => {
  const { IdToken } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  // Data fetching
  const {
    draft,
    data: apiData,
    isFetching: isLoading,
  } = useEmployeeDraft({ id: id! }, { skip: !id || operation === Operation.Create });
  const [draftState, setDraftState] = draft;

  const { isLoading: nameMapLoading } = useNameMapQuery({ token: IdToken! }, { skip: !IdToken });
  const { isLoading: locationMapLoading } = useLocationMapQuery({ token: IdToken! }, { skip: !IdToken });
  const { isLoading: orgLoading } = useOrgMapQuery({ token: IdToken! }, { skip: !IdToken });

  const [updateEmployee, { isLoading: saving }] = useUpdateEmployeeMutation();
  const [createEmployee, { isLoading: creating }] = useCreateEmployeeMutation();

  // Initialize form data
  useEffect(() => {
    if (operation === Operation.Create) {
      setDraftState({
        Name: '',
        Email: '',
        LocationID: null,
        Status: 'Active',
        CompanyID: null,
        ResellerID: null,
      });
    } else if (apiData) {
      setDraftState({
        Name: apiData.Name,
        Email: apiData.Email,
        Status: apiData.Status,
        CompanyID: apiData.CompanyID,
        ResellerID: apiData.ResellerID,
        LocationID: apiData.LocationID,
      });
    }
  }, [apiData, operation, setDraftState]);

  const handleSave = async () => {
    const employeeData = {
      Name: draftState.Name,
      Email: draftState.Email,
      LocationID: draftState.LocationID!,
      Status: draftState.Status,
    };

    if (operation === Operation.Create) {
      await createEmployee({ token: IdToken!, employee: employeeData }).unwrap();
    } else {
      await updateEmployee({ token: IdToken!, id: id!, employee: employeeData }).unwrap();
    }
    onSuccess();
  };

  const readOnly = operation === Operation.View;
  const formLoading = isLoading || saving || creating || nameMapLoading || locationMapLoading || orgLoading;
  const isFormValid = draftState.Name?.trim() && draftState.Email?.trim() && draftState.LocationID;

  const pageTitle = operation === Operation.Create
    ? 'Create New Employee'
    : (apiData?.Name || 'Employee');

  const handleViewActivity = () => {
    if (!apiData?.Email || !apiData?.LocationID || !id) return;
    
    const startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    
    navigate(
      `${AppRoutes.EmployeeDetails}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&email=${encodeURIComponent(apiData.Email)}&locationId=${encodeURIComponent(apiData.LocationID)}&from=manage&employeeId=${encodeURIComponent(id)}`
    );
  };

  return (
    <EntityPageLayout loading={formLoading && !apiData}>
      {/* Header */}
      <EntityPageHeader
        entityType="employee"
        title={pageTitle}
        icon={<BadgeIcon />}
        isCreate={operation === Operation.Create}
        loading={formLoading && !apiData}
        onCancel={onSuccess}
        onSave={!readOnly ? handleSave : undefined}
        saveDisabled={!isFormValid || saving || creating}
        saving={saving || creating}
        actions={
          operation !== Operation.Create && apiData?.Email && apiData?.LocationID ? (
            <Button variant='secondary' onClick={handleViewActivity}>
              View Activity
            </Button>
          ) : undefined
        }
      />

      {/* Employee Details Card */}
      <InfoCard title="Employee Details" icon={<InfoOutlinedIcon />}>
        <Grid container spacing={2}>
          <DraftTextField
            label="Employee Name"
            stateKey="Name"
            draft={draft}
            disabled={readOnly}
            validateFn={Validate.name}
            helperText={{
              valid: '',
              invalid: 'Please provide a valid employee name',
            }}
            size={6}
            required
          />

          <DraftTextField
            label="Email Address"
            stateKey="Email"
            draft={draft}
            disabled={operation !== Operation.Create}
            type="email"
            validateFn={(email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
            helperText={{
              valid: '',
              invalid: 'Please provide a valid email address',
            }}
            size={6}
            required
          />

          <DraftSelectField
            label="Status"
            stateKey="Status"
            draft={draft}
            disabled={readOnly}
            options={statusOptions}
            size={12}
          />
        </Grid>
      </InfoCard>

      {/* Organization Assignment Card */}
      <InfoCard title="Organization Assignment">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <OrgDropDowns
              disabled={readOnly}
              size={12}
              options={{
                companies: true,
                resellers: true,
                locations: true,
              }}
              draft={draft}
            />
          </Grid>
        </Grid>
      </InfoCard>
    </EntityPageLayout>
  );
};

export default EmployeePanel;

